import * as React from 'react';
import {memo, type ReactNode, useEffect, useState} from 'react';
import {
  useExportButtonDisabled,
  useExportEnabled,
  useExportToFigma,
  useExportToHtmlCss,
  useExportToHtmlCssWithAgentPrompt,
} from './useExportToFigma';
import {useExternalAgents} from '@features/flow/components/useExternalAgents';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@features/ui/components/dropdown-menu';
import {cn} from '@features/ui/utils';
import CodeIcon from '@/icons/Code-s.svg';
import DuplicateIcon from '@/icons/Duplicate-s.svg';
import ExportToFigmaIcon from '@/icons/Figma-s.svg';
import McpIcon from '@/icons/mcp.svg';
import ShareIcon from '@/icons/Share-s.svg';
import {useDuplicateScreenActiveStructure} from '../flowHook';
import {toast} from 'sonner';
import amplitudeService from '@features/analytics/amplitudeService';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {useShareModal} from './Share';
import {McpModal} from './McpModal';
import {AgentIcon} from '@/app/flow/components/AgentIcon';
import ChevronRightIcon from '@/icons/chevron-right-s.svg';

type Props = {
  content: ReactNode;
  tooltip: string | ReactNode;
  exportFunction: () => void;
  id?: string;
};

const CanvasExport = ({content, tooltip, exportFunction, id}: Props) => {
  const exportEnabled = useExportEnabled();
  const isDisabled = useExportButtonDisabled();

  if (!exportEnabled) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          id={id}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors',
            !isDisabled && 'hover:bg-white/20',
            isDisabled && 'opacity-50',
          )}
          onClick={exportFunction}
          disabled={isDisabled}
        >
          {content}
        </button>
      </TooltipTrigger>
      <TooltipContent sideOffset={8}>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

const CyclingAgentIcon = () => {
  const agents = useExternalAgents();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((i) => (i + 1) % agents.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [agents.length]);

  return (
    <span className='relative inline-flex size-6 items-center justify-center'>
      {agents.map((agent, i) => (
        <span
          key={agent.id}
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-opacity duration-300',
            i === activeIndex ? 'opacity-100' : 'opacity-0',
          )}
        >
          <AgentIcon agent={agent.id} />
        </span>
      ))}
    </span>
  );
};

export const CanvasExportToHtmlCss = memo(() => {
  const exportToHtmlCss = useExportToHtmlCss();
  const exportToAgentPrompt = useExportToHtmlCssWithAgentPrompt();
  const externalAgents = useExternalAgents();
  const exportEnabled = useExportEnabled();
  const isDisabled = useExportButtonDisabled();
  const [isMcpModalOpen, setIsMcpModalOpen] = useState(false);

  if (!exportEnabled) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors',
              !isDisabled && 'hover:bg-white/20',
              isDisabled && 'opacity-50',
            )}
            disabled={isDisabled}
          >
            <CodeIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          alignOffset={-14}
          sideOffset={8}
          className='min-w-[195px] [&>*]:pl-2 [&_svg]:size-auto'
        >
          <DropdownMenuItem onClick={() => setIsMcpModalOpen(true)}>
            <McpIcon />
            Export via MCP
          </DropdownMenuItem>
          <DropdownMenuItem onClick={exportToHtmlCss}>
            <DuplicateIcon />
            Copy HTML/CSS
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              icon={<ChevronRightIcon className='ml-auto !size-4 opacity-50' />}
            >
              <CyclingAgentIcon />
              Export to…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              sideOffset={12}
              alignOffset={-54}
              className='min-w-[195px] [&>*]:pl-2 [&_svg]:size-auto'
            >
              <p className='!pl-2.5 pb-1.5 pr-2.5 pt-2 text-xs leading-snug text-white/50'>
                Copy prompt and code to paste into selected app
              </p>
              {externalAgents.map((agent) => (
                <DropdownMenuItem
                  key={agent.id}
                  onClick={() => exportToAgentPrompt(agent)}
                >
                  <AgentIcon agent={agent.id} />
                  {agent.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
      <McpModal
        isOpen={isMcpModalOpen}
        onClose={() => setIsMcpModalOpen(false)}
      />
    </>
  );
});
CanvasExportToHtmlCss.displayName = 'CanvasExportToHtmlCss';

export const CanvasExportToFigmaButton = memo(() => {
  const {handleExportClick, isPending} = useExportToFigma();
  const isDisabled = useExportButtonDisabled();

  const getTooltip = () => {
    if (isPending) return 'Copying...';
    if (isDisabled) return 'Copy to Figma is not available';
    return 'Copy to Figma';
  };

  return (
    <CanvasExport
      content={<ExportToFigmaIcon />}
      tooltip={getTooltip()}
      exportFunction={handleExportClick}
    />
  );
});
CanvasExportToFigmaButton.displayName = 'CanvasExportToFigmaButton';

type CanvasDuplicateButtonProps = {
  screenId: string;
};

export const CanvasDuplicateButton = memo(
  ({screenId}: CanvasDuplicateButtonProps) => {
    const {duplicate, isPending} = useDuplicateScreenActiveStructure();
    const isGenerating = useAiThinkingForChat(screenId);
    const isDisabled = isGenerating || isPending;

    const handleDuplicate = () => {
      amplitudeService.logEvent('[Flow] screen duplicate', {source: 'canvas'});
      void toast.promise(duplicate(screenId), {
        loading: 'Duplicating screen...',
        success: 'Screen duplicated',
        error: 'Error duplicating screen, try again',
      });
    };

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors',
              !isDisabled && 'hover:bg-white/20',
              isDisabled && 'opacity-50',
            )}
            onClick={handleDuplicate}
            disabled={isDisabled}
          >
            <DuplicateIcon />
          </button>
        </TooltipTrigger>
        <TooltipContent sideOffset={8}>Duplicate screen</TooltipContent>
      </Tooltip>
    );
  },
);
CanvasDuplicateButton.displayName = 'CanvasDuplicateButton';

type CanvasShareButtonProps = {
  screenId: string;
};

export const CanvasShareButton = memo(({screenId}: CanvasShareButtonProps) => {
  const shareModal = useShareModal();

  const handleShare = () => {
    shareModal?.openWithScreen(screenId);
  };

  if (!shareModal) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className='flex h-7 w-7 items-center justify-center rounded-md text-white transition-colors hover:bg-white/20'
          onClick={handleShare}
        >
          <ShareIcon />
        </button>
      </TooltipTrigger>
      <TooltipContent sideOffset={8}>Share screen</TooltipContent>
    </Tooltip>
  );
});
CanvasShareButton.displayName = 'CanvasShareButton';
